import { Avatar, Space, Tooltip } from "antd";
import nameSort from "./name-sort";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import ImageView from "../../components/ImageView/ImageView.tsx";

export const getAverage = (result, value) => {
  if (result.statistics.games > 0) {
    return roundNumber(value / result.statistics.games);
  } else {
    return 0;
  }
};

const getStatTitle =
  ({ stat, formatMessage }) =>
  () => (
    <Tooltip title={formatMessage({ id: `stats.full.${stat}` })}>
      {formatMessage({ id: `stats.abbrev.${stat}` })}
    </Tooltip>
  );

const getSimpleStatColumnDefinition = ({ stat, formatMessage }) => ({
  title: getStatTitle({ stat, formatMessage }),
  dataIndex: ["statistics", stat],
});

export const convertClock = (clock) => {
  let clockDisplay = clock.split(".");
  clockDisplay = clockDisplay[0].replace("PT", "").replace("S", "");
  const clockParts = clockDisplay.split("M");
  clockDisplay =
    (clockParts[0].length < 2 ? "0" + clockParts[0] : clockParts[0]) + ":" + convertClockPart(clockParts[1]);
  return clockDisplay;
};

const convertClockPart = (value) => {
  switch (value.length) {
    case 1:
      return "0" + value;
    case 0:
      return "00";
    default:
      return value;
  }
};

export const roundNumber = (number) => {
  return Math.round((number + Number.EPSILON) * 100) / 100;
};

const personNameColumn = (formatMessage) => ({
  title: formatMessage({
    id: "name.local.full",
    defaultMessage: "Local Full Name",
  }),
  dataIndex: ["personObj", "nameFullLocal"],
  sorter: (a, b) => nameSort(a, b),
  defaultSortOrder: "ascend",
  render: (value, row) => (
    <Space size="small">
      <ImageView
        imageUrl={findImageByTypes(row?.personObj?.images, [ImageTypes.PersonHead])?.url}
        size={ImageSizes.Small}
        width={25}
        height={25}
        imageType={ImageTypes.PersonHead}
      />
      {value}
    </Space>
  )
});

export const getPlayerTotalColumns = ({ formatMessage }) => [
  personNameColumn(formatMessage),
  getSimpleStatColumnDefinition({ stat: "games", formatMessage }),
  {
    title: getStatTitle({ stat: "minutes", formatMessage }),
    dataIndex: ["statistics", "minutes"],
    render: (value) => convertClock(value),
  },
  {
    title: getStatTitle({ stat: "fieldGoals", formatMessage }),
    render: (value, row) => `${row.statistics.fieldGoalsMade}\xa0/\xa0${row.statistics.fieldGoalsAttempted}`,
  },
  {
    title: getStatTitle({ stat: "fieldGoalsPercentage", formatMessage }),
    dataIndex: ["statistics", "fieldGoalsPercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "pointsTwo", formatMessage }),
    render: (value, row) => `${row.statistics.pointsTwoMade}\xa0/\xa0${row.statistics.pointsTwoAttempted}`,
  },
  {
    title: getStatTitle({ stat: "pointsTwoPercentage", formatMessage }),
    dataIndex: ["statistics", "pointsTwoPercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "pointsThree", formatMessage }),
    render: (value, row) => `${row.statistics.pointsThreeMade}\xa0/\xa0${row.statistics.pointsThreeAttempted}`,
  },
  {
    title: getStatTitle({ stat: "pointsThreePercentage", formatMessage }),
    dataIndex: ["statistics", "pointsThreePercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "freeThrows", formatMessage }),
    render: (value, row) => `${row.statistics.freeThrowsMade}\xa0/\xa0${row.statistics.freeThrowsAttempted}`,
  },
  {
    title: getStatTitle({ stat: "freeThrowsPercentage", formatMessage }),
    dataIndex: ["statistics", "freeThrowsPercentage"],
    render: (value) => roundNumber(value),
  },
  ...["reboundsOffensive", "reboundsDefensive", "rebounds", "assists", "turnovers", "steals", "blocks"].map((stat) =>
    getSimpleStatColumnDefinition({ stat, formatMessage }),
  ),
  {
    title: getStatTitle({ stat: "fouls", formatMessage }),
    dataIndex: ["statistics", "foulsTotal"],
  },
  ...["foulsDrawn", "points"].map((stat) => getSimpleStatColumnDefinition({ stat, formatMessage })),
];

export const getPlayerAveragesColumns = ({ formatMessage }) => [
  personNameColumn(formatMessage),
  getSimpleStatColumnDefinition({ stat: "games", formatMessage }),
  {
    title: getStatTitle({ stat: "minutes", formatMessage }),
    dataIndex: ["statistics", "minutesPerGame"],
    render: (value) => convertClock(value),
  },
  {
    title: getStatTitle({ stat: "fieldGoals", formatMessage }),
    render: (value, row) =>
      `${getAverage(
        row,
        row.statistics.fieldGoalsMade,
      )}\xa0/\xa0${getAverage(row, row.statistics.fieldGoalsAttempted)}`,
  },
  {
    title: getStatTitle({ stat: "fieldGoalsPercentage", formatMessage }),
    dataIndex: ["statistics", "fieldGoalsPercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "pointsTwo", formatMessage }),
    render: (value, row) =>
      `${getAverage(row, row.statistics.pointsTwoMade)}\xa0/\xa0${getAverage(row, row.statistics.pointsTwoAttempted)}`,
  },
  {
    title: getStatTitle({ stat: "pointsTwoPercentage", formatMessage }),
    dataIndex: ["statistics", "pointsTwoPercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "pointsThree", formatMessage }),
    render: (value, row) =>
      `${getAverage(
        row,
        row.statistics.pointsThreeMade,
      )}\xa0/\xa0${getAverage(row, row.statistics.pointsThreeAttempted)}`,
  },
  {
    title: getStatTitle({ stat: "pointsThreePercentage", formatMessage }),
    dataIndex: ["statistics", "pointsThreePercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "freeThrows", formatMessage }),
    render: (value, row) =>
      `${getAverage(
        row,
        row.statistics.freeThrowsMade,
      )}\xa0/\xa0${getAverage(row, row.statistics.freeThrowsAttempted)}`,
  },
  {
    title: getStatTitle({ stat: "freeThrowsPercentage", formatMessage }),
    dataIndex: ["statistics", "freeThrowsPercentage"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "reboundsOffensive", formatMessage }),
    dataIndex: ["statistics", "reboundsOffensivePerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "reboundsDefensive", formatMessage }),
    dataIndex: ["statistics", "reboundsDefensivePerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "rebounds", formatMessage }),
    dataIndex: ["statistics", "reboundsPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "assists", formatMessage }),
    dataIndex: ["statistics", "assistsPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "turnovers", formatMessage }),
    dataIndex: ["statistics", "turnoversPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "steals", formatMessage }),
    dataIndex: ["statistics", "stealsPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "blocks", formatMessage }),
    dataIndex: ["statistics", "blocksPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "fouls", formatMessage }),
    dataIndex: ["statistics", "foulsPersonalPerGame"],
    render: (value) => roundNumber(value),
  },
  {
    title: getStatTitle({ stat: "foulsDrawn", formatMessage }),
    dataIndex: ["statistics", "foulsDrawn"],
    render: (value, row) => getAverage(row, value),
  },
  {
    title: getStatTitle({ stat: "points", formatMessage }),
    dataIndex: ["statistics", "pointsPerGame"],
    render: (value) => roundNumber(value),
  },
];
